import { create } from 'zustand';
import { SimpleService } from '../types';
import { supabase } from '../lib/supabase';
import { mapSupabaseService } from '../hooks/sync/syncMappers';
import { safeStorage } from '../utils/safeStorage';

const SERVICES_STORAGE_KEY = 'rmax_calendar_services';

interface ServiceStore {
  services: SimpleService[];
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;
  setServices: (services: SimpleService[]) => void;
  fetchServices: (userId?: string) => Promise<SimpleService[]>;
  saveService: (serviceData: Partial<SimpleService>) => Promise<SimpleService>;
  deleteService: (id: string) => Promise<void>;
}

const cacheServices = (services: SimpleService[]) => {
  safeStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(services));
};

export const useServiceStore = create<ServiceStore>((set, get) => ({
  services: [],
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  error: null,

  setServices: (services) => {
    set({ services, error: null });
    cacheServices(services);
  },

  fetchServices: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      const activeUserId = userId || (await supabase.auth.getSession()).data.session?.user.id;
      if (!activeUserId) throw new Error('Usuário não autenticado para carregar serviços');

      const { data, error, status } = await supabase
        .from('simple_services')
        .select('*')
        .eq('user_id', activeUserId)
        .order('created_at', { ascending: false });

      if (error || ![200, 201].includes(status) || !data) {
        throw new Error(error?.message || 'Erro ao carregar serviços no Supabase');
      }

      const services = data.map(mapSupabaseService);
      get().setServices(services);
      return services;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar serviços';
      set({ error: message });
      throw error instanceof Error ? error : new Error(message);
    } finally {
      set({ isLoading: false });
    }
  },

  saveService: async (serviceData) => {
    set({ isSaving: true, error: null });
    const id = serviceData.id || crypto.randomUUID();
    const allowedStaff = serviceData.allowedStaffIds || [];
    
    const fallbackLocalService: SimpleService = {
      id,
      userId: 'local_user',
      name: serviceData.name?.trim() || '',
      value: Number(serviceData.value || 0),
      type: serviceData.type || 'fixed',
      description: serviceData.description,
      promo: serviceData.promo,
      allowedStaffIds: allowedStaff,
      createdAt: serviceData.createdAt || new Date().toISOString()
    };

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session?.user.id) {
        fallbackLocalService.userId = session.user.id;
        const enrichedPromoConfig = {
          ...(serviceData.promo || {}),
          allowedStaffIds: allowedStaff
        };

        const basePayload: any = {
          id,
          user_id: session.user.id,
          name: serviceData.name?.trim() || '',
          value: Number(serviceData.value || 0),
          type: serviceData.type || 'fixed',
          description: serviceData.description,
          promo_config: serviceData.promo ? enrichedPromoConfig : (allowedStaff.length > 0 ? { allowedStaffIds: allowedStaff } : null),
          created_at: serviceData.createdAt || new Date().toISOString()
        };

        let { data, error, status } = await supabase
          .from('simple_services')
          .upsert({ ...basePayload, allowed_staff_ids: allowedStaff })
          .select()
          .single();

        if (error && (error.message?.includes('allowed_staff_ids') || error.code === '42703' || error.code === 'PGRST204')) {
          // Fallback caso a coluna ainda não exista no Supabase
          const fallbackRes = await supabase
            .from('simple_services')
            .upsert(basePayload)
            .select()
            .single();
          data = fallbackRes.data;
          error = fallbackRes.error;
          status = fallbackRes.status;
        }

        if (data && [200, 201].includes(status)) {
          const savedService = mapSupabaseService(data);
          const services = [savedService, ...get().services.filter(service => service.id !== savedService.id)];
          set({ services, error: null });
          cacheServices(services);
          return savedService;
        }
      }

      // Offline / Fallback local caso sessão esteja ausente ou banco desconectado
      const services = [fallbackLocalService, ...get().services.filter(service => service.id !== fallbackLocalService.id)];
      set({ services, error: null });
      cacheServices(services);
      return fallbackLocalService;
    } catch (error) {
      console.warn('Salvando serviço em modo offline/local:', error);
      const services = [fallbackLocalService, ...get().services.filter(service => service.id !== fallbackLocalService.id)];
      set({ services, error: null });
      cacheServices(services);
      return fallbackLocalService;
    } finally {
      set({ isSaving: false });
    }
  },

  deleteService: async (id) => {
    set({ isDeleting: true, error: null });
    try {
      const { error, status } = await supabase
        .from('simple_services')
        .delete()
        .eq('id', id);

      if (error || ![200, 204].includes(status)) {
        throw new Error(error?.message || 'Erro ao excluir serviço no Supabase');
      }

      const services = get().services.filter(service => service.id !== id);
      set({ services, error: null });
      cacheServices(services);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir serviço no Supabase';
      set({ error: message });
      throw error instanceof Error ? error : new Error(message);
    } finally {
      set({ isDeleting: false });
    }
  }
}));